import { Check, ChevronDown, Crown, LogOut, Plus, Trash2, UserRound, WalletCards } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAppState } from "./app-state";
import type { CreatorProfile } from "./lib/launchpad";
import { shortenAddress } from "./lib/wallets";
import { applyPixelArtRendering } from "./lib/pixel-art";
import { MARKETPLACE_THUMBNAIL_RENDER_VERSION } from "../shared/marketplace-media-version";

type AvatarSource = "thumbnail" | "direct" | "document";

function profileName(profile: CreatorProfile): string {
  return profile.username;
}

function AccountAvatar({ profile }: { profile: CreatorProfile | null }) {
  const [source, setSource] = useState<AvatarSource>("thumbnail");
  useEffect(() => setSource("thumbnail"), [profile?.avatarInscriptionId]);

  if (profile?.avatarInscriptionId) {
    const id = profile.avatarInscriptionId;
    return <span className={`account-avatar${source === "document" ? " document" : ""}`}>
      {source === "document"
        ? <iframe src={`https://ordinals.com/content/${id}`} title={`${profileName(profile)} profile picture`} loading="lazy" sandbox="allow-scripts allow-same-origin" referrerPolicy="no-referrer" />
        : <img
            src={source === "thumbnail" ? `/api/marketplace/thumbnail/${encodeURIComponent(id)}?v=${MARKETPLACE_THUMBNAIL_RENDER_VERSION}` : `https://ordinals.com/content/${id}`}
            alt=""
            onLoad={(event) => applyPixelArtRendering(event.currentTarget)}
            onError={() => setSource((current) => current === "thumbnail" ? "direct" : "document")}
          />}
    </span>;
  }
  if (profile?.xAvatarUrl) return <span className="account-avatar"><img src={profile.xAvatarUrl} alt="" referrerPolicy="no-referrer" /></span>;
  return <span className="account-avatar fallback"><UserRound /></span>;
}

export function AccountControl() {
  const {
    account,
    accountBusy,
    accountError,
    clearAccountError,
    creatorProfile,
    disconnectWallet,
    linkedWallets,
    makeMainWallet,
    profileLoading,
    reconnectWallet,
    removeLinkedWallet,
    setWalletModalMode,
    setWalletModalOpen,
    switchWallet,
    wallet,
  } = useAppState();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const hasAccount = !!wallet || !!account;
  const label = creatorProfile ? profileName(creatorProfile) : wallet ? shortenAddress(wallet.payment.address) : account ? "RECONNECT WALLET" : "CONNECT WALLET";
  const detail = creatorProfile
    ? `@${creatorProfile.xUsername || creatorProfile.username}`
    : wallet
      ? wallet.provider.toUpperCase()
      : "XVERSE · UNISAT";
  const wallets = account?.wallets ?? (wallet ? [{
    paymentAddress: wallet.payment.address,
    ordinalsAddress: wallet.ordinals.address,
    paymentPublicKey: wallet.payment.publicKey,
    ordinalsPublicKey: wallet.ordinals.publicKey,
    provider: wallet.provider,
    isPrimary: true,
    createdAt: 0,
  }] : []);
  const localAddresses = new Set(linkedWallets.map((item) => item.payment.address.toLowerCase()));

  const openLinkWallet = () => {
    clearAccountError();
    setOpen(false);
    setWalletModalMode("link");
    setWalletModalOpen(true);
  };

  return <div className="account-control" ref={root}>
    <button
      aria-label={hasAccount ? `Account: ${label}` : "Connect wallet"}
      className={`account-button${hasAccount ? " connected" : ""}`}
      type="button"
      aria-haspopup={hasAccount ? "menu" : undefined}
      aria-expanded={hasAccount ? open : undefined}
      onClick={() => hasAccount ? setOpen((current) => !current) : setWalletModalOpen(true)}
    >
      {hasAccount ? <AccountAvatar profile={creatorProfile} /> : <span className="account-avatar fallback"><WalletCards /></span>}
      <span className="account-identity"><strong>{profileLoading && !creatorProfile ? "LOADING PROFILE" : label}</strong><small>{detail}</small></span>
      {hasAccount ? <ChevronDown className={open ? "open" : ""} /> : null}
    </button>
    {hasAccount && open ? <div className="account-menu" role="menu">
      <header><AccountAvatar profile={creatorProfile} /><span><strong>{label}</strong><small>{wallet ? shortenAddress(wallet.payment.address) : "Select a remaining wallet"}</small></span></header>
      <Link role="menuitem" to="/launchpad/profile"><UserRound /> {creatorProfile ? "EDIT PROFILE" : "CREATE PROFILE"}</Link>
      {creatorProfile ? <Link role="menuitem" to={`/launchpad/artist/${creatorProfile.username}`}><UserRound /> VIEW PUBLIC PROFILE</Link> : null}
      <section className="account-wallet-list">
        <header><span>WALLETS</span><button type="button" disabled={accountBusy} onClick={openLinkWallet}><Plus /> ADD WALLET</button></header>
        {wallets.map((item) => {
          const active = item.paymentAddress.toLowerCase() === wallet?.payment.address.toLowerCase();
          const local = localAddresses.has(item.paymentAddress.toLowerCase());
          return <article className={active ? "active" : ""} key={item.paymentAddress}>
            <button type="button" disabled={accountBusy || active} onClick={() => local ? switchWallet(item.paymentAddress) : void reconnectWallet(item.paymentAddress).catch(() => undefined)}>
              <span className="account-wallet-state">{active ? <Check /> : <WalletCards />}</span>
              <span><strong>{shortenAddress(item.paymentAddress)}</strong><small>{item.isPrimary ? "MAIN WALLET" : local ? item.provider?.toUpperCase() ?? "LINKED" : "RECONNECT"}</small></span>
            </button>
            <span className="account-wallet-actions">
              {!item.isPrimary ? <button type="button" title="Make main wallet" aria-label={`Make ${item.paymentAddress} the main wallet`} disabled={accountBusy} onClick={() => void makeMainWallet(item.paymentAddress).catch(() => undefined)}><Crown /></button> : <i title="Main wallet"><Crown /></i>}
              {!item.isPrimary ? <button type="button" title="Remove wallet" aria-label={`Remove ${item.paymentAddress}`} disabled={accountBusy} onClick={() => void removeLinkedWallet(item.paymentAddress).catch(() => undefined)}><Trash2 /></button> : null}
            </span>
          </article>;
        })}
        {accountError ? <p>{accountError}</p> : null}
      </section>
      <Link role="menuitem" to="/history"><span>01</span> MY INSCRIPTIONS</Link>
      <button role="menuitem" type="button" onClick={disconnectWallet}><LogOut /> SIGN OUT</button>
    </div> : null}
  </div>;
}

export function AccountWalletSelector({ label = "VIEWING WALLET", iconOnly = false }: { label?: string; iconOnly?: boolean }) {
  const { account, accountBusy, linkedWallets, reconnectWallet, switchWallet, wallet } = useAppState();
  if (!wallet) return null;
  const records = account?.wallets ?? linkedWallets.map((item, index) => ({
    paymentAddress: item.payment.address,
    ordinalsAddress: item.ordinals.address,
    paymentPublicKey: item.payment.publicKey,
    ordinalsPublicKey: item.ordinals.publicKey,
    provider: item.provider,
    isPrimary: index === 0,
    createdAt: 0,
  }));
  const localAddresses = new Set(linkedWallets.map((item) => item.payment.address.toLowerCase()));
  return <label className={iconOnly ? "account-wallet-selector icon-only" : "account-wallet-selector"}>
    <span aria-hidden={iconOnly ? "true" : undefined}>{iconOnly ? <WalletCards /> : label}</span>
    <select disabled={accountBusy} value={wallet.payment.address} onChange={(event) => {
      const address = event.target.value;
      if (localAddresses.has(address.toLowerCase())) switchWallet(address);
      else void reconnectWallet(address).catch(() => undefined);
    }} aria-label={label}>
      {records.map((item) => <option value={item.paymentAddress} key={item.paymentAddress}>
        {shortenAddress(item.paymentAddress)}{item.isPrimary ? " · MAIN" : ""}{localAddresses.has(item.paymentAddress.toLowerCase()) ? "" : " · RECONNECT"}
      </option>)}
    </select>
  </label>;
}
